import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Space,
  Modal,
  InputNumber,
  Select
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

interface GatePassModalProps {
  visible: boolean;
  onCancel: () => void;
  onCreate: (data: any) => void;
  initialValues?: {
    gatepass_id?: number;
    gatepass_student_id?: number;
    gatepass_approve_by?: number;
    gatepass_relation?: string;
    gatepass_receiver_name?: string;
    gatepass_detail?: string;
  } | null;
  isEditMode?: boolean;
}

const GatePassModal: React.FC<GatePassModalProps> = ({
  visible,
  onCancel,
  onCreate,
  initialValues = null,
  isEditMode = false,
}) => {
  const [form] = Form.useForm();
  const [isModalClose, setIsModalClose] = useState(true);

  const [studentOptions, setStudentOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);

  const isSubmitDisabled = () => {
    const errors = form.getFieldsError();
    return errors.some(({ errors }) => errors.length > 0);
  };
  const { TextArea } = Input;

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        gatepass_receiver_name: values.gatepass_receiver_name,
        gatepass_relation: values.gatepass_relation,
        gatepass_mobile: values.gatepass_mobile,
        gatepass_student_id: values.gatepass_student_id,
        gatepass_approve_by: values.gatepass_approve_by,
        gatepass_detail: values.gatepass_detail,
        ...(initialValues?.gatepass_id && { gatepass_id: initialValues.gatepass_id }),
      };

      const url = isEditMode ? 'edit-gatepass' : 'add-gatepass';
      const method = isEditMode ? 'put' : 'post';

      const data = await apiRequest({
        method,
        url,
        data: payload,
      });

      onCreate(data);
      showSuccess(
        isEditMode ? 'gate pass updated successfully!' : 'gate pass created successfully!'
      );
      form.resetFields();

      if (isModalClose) {
        onCancel();
      }
    } catch (error) {
      showError(`Failed to ${isEditMode ? 'update' : 'create'} gate pass`);
    }
  };
  const fetchData = async () => {
    setLoading(true);
    try {
      const [studnetRes, userRes] = await Promise.all([
        apiRequest({ method: 'get', url: '/students' }),
        apiRequest({ method: 'get', url: '/users' }),
      ]);
      setStudentOptions(studnetRes);
      setUserOptions(userRes);
    } catch {
      showError('Failed to load class or user data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && isEditMode && initialValues) {
      form.setFieldsValue(initialValues);
    } else if (visible) {
      form.resetFields();
    }
    fetchData()
  }, [visible, isEditMode, initialValues, form]);

  return (
    <Modal
      title={isEditMode ? 'Edit Gate Pass' : 'Add New Gate Pass'}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width="90%"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="gatepass_receiver_name"
              className="px-8"
              label={
                <span>
                  Receiver Name&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 255 characters)
                  </span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the Receiver Name' },
                { max: 255, message: 'Maximum 255 characters allowed' },
              ]}
            >
              <Input
                showCount
                maxLength={255}
                placeholder="e.g., Ajeet"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gatepass_relation"
              className="px-8"
              label={
                <span>
                  Relation&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 25 characters)
                  </span>
                </span>
              }
              rules={[
                { required: true, message: 'Please enter the relation' },
                { max: 25, message: 'Maximum 25 characters allowed' },
              ]}
            >
              <Input
                showCount
                maxLength={25}
                placeholder="e.g., Father, Mother, etc."
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gatepass_mobile"
              className="px-8"
              label={
                <span>
                  Receiver Mobile Number&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    (Max 10 Digit)
                  </span>
                </span>
              }
            >
              <InputNumber
                className="py-7 border-radious"
                placeholder="Enter Mobile Number"
                maxLength={10}
                minLength={10}
                style={{ width: '100%' }}
                controls={false}
                onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                  const nativeEvent = e.nativeEvent as InputEvent;
                  const inputChar = nativeEvent.data;
                  const currentValue = (e.target as HTMLInputElement).value;
                  if (!inputChar || !/^\d$/.test(inputChar) || currentValue.length >= 10) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gatepass_student_id"
              label="Student Name"
              className="px-8"
              rules={[{ required: true, message: 'Please select a class' }]}
            >
              <Select placeholder="Select Student" loading={loading}
                showSearch
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? '')
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? '').toLowerCase())
                }
                options={studentOptions?.map((st) => ({
                  label: st.student_name,
                  value: st.student_id,
                }))}

                optionFilterProp="label"
                allowClear>
                {studentOptions.map((st) => (
                  <Select.Option key={st.student_id} value={st.student_id}>
                    {st.student_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="gatepass_approve_by"
              label="Select Parent"
              className="px-8"
              rules={[{ required: true, message: 'Please select a user' }]}
            >
              <Select placeholder="Select User" loading={loading}
                showSearch
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? '')
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? '').toLowerCase())
                }
                options={userOptions?.map((user) => ({
                  label: user.user_name,
                  value: user.user_id,
                }))}

                optionFilterProp="label"
                allowClear>
                {userOptions.map((user) => (
                  <Select.Option key={user.user_id} value={user.user_id}>
                    {user.user_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="gatepass_detail"
              className="px-8"
              label={
                <span>
                  Detail&nbsp;
                  <span style={{ fontSize: '12px', color: '#888' }}>(Max 255 characters)</span>
                </span>
              }
              rules={[{ required: true, message: 'Please enter the Student Address' }]}
            >
              <TextArea maxLength={255} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            {!isEditMode && (
              <Button
                type="primary"
                htmlType="submit"
                onClick={() => setIsModalClose(false)}
                disabled={isSubmitDisabled()}
              >
                Submit & Create New Gate Pass
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              onClick={() => setIsModalClose(true)}
              disabled={isSubmitDisabled()}
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GatePassModal;
